import React from "react";
import { createPortal } from "react-dom";
import OperationalStepList from "./OperationalStepList.jsx";
import {
  EmptyState,
  PanelShell,
  StatusBadge,
  DISPATCH_DARK_HEADER_CHIP_CLASS_NAME,
  DISPATCH_DARK_HEADER_CLASS_NAME,
  DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME,
} from "./ResultsPrimitives.jsx";
import { resolveFleetCardViewModel } from "../utils/resultsFleetCardViewModel.js";
import { buildFleetSequenceDisplayItems } from "../utils/fleetTimelinePresentation.js";
import { buildTripTimelineSegments } from "../utils/routeModelsData.js";

function resolveDisplayTravelMinutes(segment) {
  const displayTravelMinutes = Number(segment?.displayTravelDurationMinutes);
  return Number.isFinite(displayTravelMinutes) && displayTravelMinutes >= 0
    ? displayTravelMinutes
    : Math.max(Number(segment?.travelDurationMinutes) || 0, 0);
}

// Même part foncée que le Gantt : service ÷ (service + trajet affiché + tampon manuel),
// après la même fusion du retour au dépôt ; les arrêts d'un même site partagent la part de leur segment.
function buildServicePercentByStepKey(assignment, state) {
  const servicePercentByStepKey = new Map();
  (Array.isArray(assignment?.trips) ? assignment.trips : []).filter(Boolean).forEach((trip) => {
    const groups = [];
    buildFleetSequenceDisplayItems(buildTripTimelineSegments(trip, assignment, { state })).forEach((segment) => {
      const groupKey = typeof segment?.sameSiteGroupKey === "string" ? segment.sameSiteGroupKey.trim() : "";
      const previousGroup = groups[groups.length - 1];
      if (groupKey && previousGroup?.groupKey === groupKey) {
        previousGroup.members.push(segment);
      } else {
        groups.push({ groupKey, members: [segment] });
      }
    });
    groups.forEach(({ members }) => {
      const sumMinutes = (readMinutes) => members.reduce((total, member) => total + readMinutes(member), 0);
      const serviceMinutes = sumMinutes((member) => Math.max(Number(member?.serviceDurationMinutes) || 0, 0));
      const totalMinutes = serviceMinutes
        + sumMinutes(resolveDisplayTravelMinutes)
        + sumMinutes((member) => Math.max(Number(member?.manualBufferMinutes) || 0, 0));
      if (serviceMinutes > 0 && totalMinutes > 0) {
        members.forEach((member) => {
          servicePercentByStepKey.set(`${trip.trip_id || ""}::${member.globalSequence}`, (serviceMinutes / totalMinutes) * 100);
        });
      }
    });
  });
  return servicePercentByStepKey;
}

export default function RouteAssignmentStepsPanel({ card = null, mountNode = null, state = null }) {
  const viewModel = resolveFleetCardViewModel(card || {});
  const assignment = card ? viewModel.assignment : null;
  const servicePercentByStepKey = React.useMemo(
    () => (assignment ? buildServicePercentByStepKey(assignment, state) : null),
    [assignment, state],
  );

  if (!(mountNode instanceof HTMLElement) || !card) {
    return null;
  }

  const tripGroups = Array.isArray(viewModel.tripGroups) ? viewModel.tripGroups.filter(Boolean) : [];
  const statusBadge = viewModel.shell?.statusBadge && viewModel.shell.statusBadge.isHidden !== true
    ? viewModel.shell.statusBadge
    : null;
  const flattenedOperationalSteps = tripGroups.flatMap((group) => (
    Array.isArray(group?.operationalSteps) ? group.operationalSteps : []
  ));
  const driverName = typeof assignment?.driver_name === "string" ? assignment.driver_name.trim() : "";

  const content = (
    <section className="hidden h-full min-h-0 min-[841px]:grid min-[841px]:max-h-full">
      <PanelShell
        title={viewModel.shell?.truckTitle || "Camion sélectionné"}
        description={driverName}
        actions={(
          <>
            {statusBadge ? <StatusBadge tone={statusBadge.tone} className={DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME}>{statusBadge.label}</StatusBadge> : null}
            {flattenedOperationalSteps.length ? (
              <span className={DISPATCH_DARK_HEADER_CHIP_CLASS_NAME}>{flattenedOperationalSteps.length} étapes</span>
            ) : null}
            {viewModel.distanceSummary ? (
              <span className={DISPATCH_DARK_HEADER_CHIP_CLASS_NAME} data-route-pill="true">
                {viewModel.distanceSummary}
              </span>
            ) : null}
          </>
        )}
        actionsClassName="gap-1!"
        className="h-full min-h-0 max-h-full content-start grid-rows-[auto_minmax(0,1fr)] gap-1 overflow-hidden rounded-[4px] border border-[#d9d2ca] bg-white p-1 shadow-[0_1px_2px_rgba(57,49,42,0.08)]"
        bodyClassName="grid min-h-0 content-start gap-1 overflow-hidden"
        copyClassName="gap-0!"
        descriptionClassName="text-[0.66rem]! leading-[1.2]! text-[#eee9e3]!"
        headerClassName={`gap-1! lg:items-center ${DISPATCH_DARK_HEADER_CLASS_NAME}`}
        titleClassName="text-[0.98rem]! leading-[1.1]! tracking-[-0.02em]! text-white!"
      >
        {!flattenedOperationalSteps.length ? (
          <EmptyState
            tone="subtle"
            title="Aucune étape à afficher"
            message="Ce camion ne porte actuellement aucune étape opérationnelle exploitable dans la vue Route."
          />
        ) : (
          <div
            className="grid min-h-0 auto-rows-min content-start gap-1 overflow-y-auto overscroll-contain pr-1"
            onWheelCapture={(event) => {
              event.stopPropagation();
            }}
          >
            <OperationalStepList
              interactions={card.interactions || {}}
              servicePercentByStepKey={servicePercentByStepKey}
              steps={flattenedOperationalSteps}
            />
          </div>
        )}
      </PanelShell>
    </section>
  );

  return createPortal(content, mountNode);
}
